/**
 * Videocall Recording Monitors + Audio Pipeline
 *
 * silence/speech detection helpers for the last-resort MediaRecorder fallback,
 * plus the main streaming audio pipeline (AudioWorklet / ScriptProcessor).
 */
import { getVideocallMicrophoneStream } from './videocallMicrophone';
const $ = jQuery;

// ── Monitor helpers ────────────────────────────────────────────────

export function monitorSilence(ctx) {
    const { audioRecorder, getCurrentAudioLevel, silenceThreshold, silenceDelay,
            recordingWasTriggered, debugLog, onSilenceTimeout } = ctx;

    if (!audioRecorder) { debugLog("No audio Recorder available"); return; }

    if (ctx.assistantAudioActive) {
        if (ctx._silenceTimer) { clearTimeout(ctx._silenceTimer); ctx._silenceTimer = null; }
        ctx.userSaidSomethingCounter = 0;
        return;
    }

    if (audioRecorder.state !== "recording") {
        if (window.silenceMonitorInterval) {
            clearInterval(window.silenceMonitorInterval);
            window.silenceMonitorInterval = null;
        }
        return;
    }

    const level = getCurrentAudioLevel();

    if (level < silenceThreshold) {
        if (!ctx._silenceTimer) {
            ctx._silenceTimer = setTimeout(() => {
                if (ctx.assistantAudioActive) {
                    debugLog('Silence completion ignored while assistant audio is active');
                    ctx.userSaidSomethingCounter = 0;
                    ctx._silenceTimer = null;
                    return;
                }
                if (audioRecorder && audioRecorder.state === "recording") {
                    if (ctx.userSaidSomethingCounter > 5) {
                        try {
                            if (recordingWasTriggered) {
                                onSilenceTimeout();
                                ctx.userSaidSomethingCounter = 0;
                            }
                        } catch (error) { console.error("Error requesting audio data:", error); }
                    }
                } else { debugLog("AudioRecorder not in recording state, skipping requestData"); }
                ctx._silenceTimer = null;
            }, silenceDelay);
        }
    } else {
        if (ctx._silenceTimer) { clearTimeout(ctx._silenceTimer); ctx._silenceTimer = null; }
    }
}

export function monitorSpeech(ctx) {
    if (!ctx.audioRecorder) { ctx.debugLog?.("No audioRecorder available"); return; }
    if (ctx.assistantAudioActive) return;
    const level = ctx.getCurrentAudioLevel();
    if (level > ctx.speechThreshold) {
        if (ctx.userSaidSomethingCounter === 0 && ctx.onSpeechDetected) ctx.onSpeechDetected();
        ctx.userSaidSomethingCounter++;
    }
}

export async function initMic(debugLog) {
    try {
        const stream = await getVideocallMicrophoneStream(debugLog);
        const track = stream.getAudioTracks()[0];
        debugLog("Microphone access granted", {
            hasTrack: !!track,
            enabled: track ? track.enabled : null,
            muted: track ? track.muted : null,
            readyState: track ? track.readyState : null,
            settings: track && typeof track.getSettings === 'function' ? track.getSettings() : null,
        });
    } catch (e) {
        console.error("[growtype-chat][videocall] Microphone access failed", {
            name: e && e.name,
            message: e && e.message,
        });
    }
}

// ── Audio Recording Pipeline ───────────────────────────────────────

export async function setupAudioRecording(ctx) {
    ctx.debugLog("Audio recording setup started", {
        supportsMediaDevices: ctx.supportsMediaDevices,
        mediaRecorderAvailable: typeof window.MediaRecorder !== 'undefined',
    });
    if (!ctx.supportsMediaDevices) { console.warn("Audio recording not supported."); return false; }

    try {
        const stream = await getVideocallMicrophoneStream(ctx.debugLog);
        if (stream) {
            const track = stream.getAudioTracks()[0];
            ctx.debugLog(`🎙️ Active Mic: ${track ? track.label : 'Unknown'}`);
            if (track && !track.enabled) track.enabled = true;
        }

        const audioCtx = new (window.AudioContext || window.webkitAudioContext)({ sampleRate: 24000 });
        if (audioCtx.state === 'suspended') { ctx.debugLog('🔊 Resuming AudioContext...'); await audioCtx.resume(); }

        const supportsAudioWorklet = !!audioCtx.audioWorklet && typeof AudioWorkletNode === 'function';
        const supportsScriptProcessor = typeof audioCtx.createScriptProcessor === 'function';
        // Both PCM pipelines stream continuously. The provider's server VAD is
        // the single authority for turn boundaries and barge-in decisions.
        const useRealtimeAudioWorklet = supportsAudioWorklet;
        ctx.debugLog("Audio recording capabilities resolved", {
            audioContextState: audioCtx.state,
            audioContextSampleRate: audioCtx.sampleRate,
            supportsAudioWorklet: supportsAudioWorklet,
            supportsScriptProcessor: supportsScriptProcessor,
            isAppleMobileDevice: /iPad|iPhone|iPod/.test(navigator.userAgent),
            selectedPipeline: useRealtimeAudioWorklet ? 'AudioWorklet' : (supportsScriptProcessor ? 'ScriptProcessor' : 'MediaRecorder'),
        });

        if (useRealtimeAudioWorklet) {
            ctx.debugLog("🎯 Using AudioWorklet for real-time PCM streaming");
            await _setupAudioWorkletRecording(stream, audioCtx, ctx);
        } else if (supportsScriptProcessor) {
            ctx.debugLog("🎯 Using ScriptProcessor for compatible real-time PCM streaming");
            _setupScriptProcessorRecording(stream, audioCtx, ctx);
        } else {
            ctx.debugLog("📦 Using last-resort MediaRecorder fallback");
            _setupMediaRecorderFallback(stream, audioCtx, ctx);
        }
        return true;
    } catch (err) {
        console.error("Failed to access microphone:", err);
        if (typeof ctx.onRecordingError === 'function') ctx.onRecordingError(err);
        return false;
    }
}

async function _setupAudioWorkletRecording(stream, audioCtx, ctx) {
    try {
        const workletPath = window.location.origin + '/app/plugins/growtype-chat/resources/scripts/partials/worklets/pcm16-processor.js';
        ctx.debugLog('🔧 Loading AudioWorklet from:', workletPath);
        await audioCtx.audioWorklet.addModule(workletPath);

        const source = audioCtx.createMediaStreamSource(stream);
        const workletNode = new AudioWorkletNode(audioCtx, 'pcm16-processor');
        let isStreaming = false;

        workletNode.port.onmessage = (event) => {
            if (!isStreaming || event.data.type !== 'audio') return;
            const int16Array = new Int16Array(event.data.data);
            _sendPcmToProvider(int16Array, ctx);
        };

        window.startAudioWorklet = async () => {
            if (audioCtx.state === 'suspended') await audioCtx.resume();
            source.connect(workletNode); workletNode.connect(audioCtx.destination);
            isStreaming = true;
            ctx.debugLog('Provider-authoritative microphone streaming started');
        };
        window.stopAudioWorklet = () => {
            isStreaming = false; source.disconnect(); workletNode.disconnect();
            ctx.debugLog('Provider-authoritative microphone streaming stopped');
        };
    } catch (error) {
        console.error('❌ AudioWorklet setup failed:', error);
        if (typeof audioCtx.createScriptProcessor === 'function') {
            ctx.debugLog('Falling back to compatible real-time ScriptProcessor streaming');
            _setupScriptProcessorRecording(stream, audioCtx, ctx);
        } else {
            _setupMediaRecorderFallback(stream, audioCtx, ctx);
        }
    }
}

function _sendPcmToProvider(int16Array, ctx) {
    if (!ctx.socket.val) return;
    if (!ctx.sessionStarted) { ctx.debugLog('⏳ Waiting for session_started...'); return; }
    if (ctx.socket.val.readyState !== WebSocket.OPEN) return;

    const bytes = new Uint8Array(int16Array.buffer, int16Array.byteOffset, int16Array.byteLength);
    const base64 = btoa(String.fromCharCode.apply(null, bytes));
    ctx.sendToWs({
        session_token: ctx.sessionToken,
        message: base64,
        timestamp: Date.now(),
        type: 'audio_stream',
    });
}

function _float32ToPcm16(floatSamples) {
    const pcm = new Int16Array(floatSamples.length);
    for (let i = 0; i < floatSamples.length; i++) {
        const sample = Math.max(-1, Math.min(1, floatSamples[i]));
        pcm[i] = sample < 0 ? sample * 0x8000 : sample * 0x7fff;
    }
    return pcm;
}

function _createStreamingResampler(sourceSampleRate, targetSampleRate = 24000) {
    const sourceRate = Number(sourceSampleRate) || targetSampleRate;
    const ratio = sourceRate / targetSampleRate;
    let pendingInput = new Float32Array(0);
    let nextPosition = 0;

    return function resample(input) {
        if (ratio === 1 || !input.length) return input;

        const combined = new Float32Array(pendingInput.length + input.length);
        combined.set(pendingInput, 0);
        combined.set(input, pendingInput.length);

        const output = [];
        while (nextPosition < combined.length - 1) {
            const index = Math.floor(nextPosition);
            const fraction = nextPosition - index;
            output.push(combined[index] + ((combined[index + 1] - combined[index]) * fraction));
            nextPosition += ratio;
        }

        const consumed = Math.floor(nextPosition);
        pendingInput = combined.slice(Math.min(consumed, combined.length));
        nextPosition -= consumed;

        return Float32Array.from(output);
    };
}

/**
 * Broad compatibility streaming fallback for browsers without AudioWorklet.
 * Unlike MediaRecorder, this still lets provider VAD hear the microphone while
 * the assistant is speaking and make the interruption decision server-side.
 */
function _setupScriptProcessorRecording(stream, audioCtx, ctx) {
    const source = audioCtx.createMediaStreamSource(stream);
    const processor = audioCtx.createScriptProcessor(4096, 1, 1);
    const resampleToProviderRate = _createStreamingResampler(audioCtx.sampleRate, 24000);
    let isStreaming = false;

    processor.onaudioprocess = (event) => {
        if (!isStreaming) return;
        const input = event.inputBuffer && event.inputBuffer.getChannelData(0);
        if (!input) return;
        _sendPcmToProvider(_float32ToPcm16(resampleToProviderRate(input)), ctx);
    };

    window.startScriptProcessor = async () => {
        if (audioCtx.state === 'suspended') await audioCtx.resume();
        source.connect(processor);
        processor.connect(audioCtx.destination);
        isStreaming = true;
        ctx.debugLog('Provider-authoritative compatible microphone streaming started');
    };
    window.stopScriptProcessor = () => {
        isStreaming = false;
        try { source.disconnect(); } catch (error) {}
        try { processor.disconnect(); } catch (error) {}
        ctx.debugLog('Provider-authoritative compatible microphone streaming stopped');
    };
}

function _setupMediaRecorderFallback(stream, audioCtx, ctx) {
    ctx.debugLog("MediaRecorder fallback initialization entered", {
        monitorSilenceAvailable: typeof ctx.monitorSilenceFn === 'function',
        monitorSpeechAvailable: typeof ctx.monitorSpeechFn === 'function',
        transcriptionAuthority: 'provider',
    });
    let mimeType = '';
    for (const t of ['audio/wav','audio/webm;codecs=opus','audio/mp4','audio/aac','audio/ogg;codecs=opus','audio/webm']) {
        if (MediaRecorder.isTypeSupported(t)) { mimeType = t; break; }
    }
    ctx.audioRecorder.val = new MediaRecorder(stream, { mimeType: mimeType || undefined, audioBitsPerSecond: 128000 });
    ctx.debugLog("MediaRecorder fallback created", {
        requestedMimeType: mimeType || null,
        actualMimeType: ctx.audioRecorder.val.mimeType || null,
        state: ctx.audioRecorder.val.state,
    });
    let silenceMon, speechMon, audioChunks = [];
    let recordingStartedAt = 0;

    ctx.audioRecorder.val.onstart = () => {
        recordingStartedAt = Date.now();
        ctx.debugLog("MediaRecorder started", {
            state: ctx.audioRecorder.val.state,
            mimeType: ctx.audioRecorder.val.mimeType || null,
        });
        if (silenceMon) clearInterval(silenceMon); if (speechMon) clearInterval(speechMon);
        audioChunks = [];
        silenceMon = setInterval(() => ctx.monitorSilenceFn(), 200);
        speechMon = setInterval(() => ctx.monitorSpeechFn(), 200);
    };

    ctx.audioRecorder.val.ondataavailable = (event) => {
        ctx.debugLog("MediaRecorder data available", {
            size: event.data.size,
            type: event.data.type,
        });
        if (event.data.size > 0) audioChunks.push(event.data);
    };

    ctx.audioRecorder.val.onstop = async () => {
        const shouldDiscardRecordedAudio = Boolean(ctx.discardCurrentRecording);
        ctx.discardCurrentRecording = false;
        ctx.debugLog("MediaRecorder stopped", {
            chunkCount: audioChunks.length,
            socketState: ctx.socket.val ? ctx.socket.val.readyState : null,
            recordingDurationMs: recordingStartedAt ? Date.now() - recordingStartedAt : null,
            discardedForAssistantPlayback: shouldDiscardRecordedAudio,
        });
        if (silenceMon) { clearInterval(silenceMon); silenceMon = null; }
        if (speechMon) { clearInterval(speechMon); speechMon = null; }
        const canSendRecordedAudio = !shouldDiscardRecordedAudio && audioChunks.length > 0 && ctx.socket.val && ctx.socket.val.readyState === WebSocket.OPEN;
        ctx.debugLog('MediaRecorder send eligibility resolved', {
            canSendRecordedAudio: !!canSendRecordedAudio,
            discardedForAssistantPlayback: shouldDiscardRecordedAudio,
            chunkCount: audioChunks.length,
            socketPresent: !!ctx.socket.val,
            socketState: ctx.socket.val ? ctx.socket.val.readyState : null,
        });
        if (canSendRecordedAudio) {
            try {
                const ab = new Blob(audioChunks, { type: audioChunks[0].type });
                ctx.debugLog("Decoding MediaRecorder audio", {
                    size: ab.size,
                    type: ab.type,
                });
                const buf = await audioCtx.decodeAudioData(await ab.arrayBuffer());
                ctx.debugLog('MediaRecorder audio decoded', { duration: buf.duration, sampleRate: buf.sampleRate });
                const wav = ctx.audioBufferToWav(buf, 24000);
                ctx.debugLog('MediaRecorder audio converted to WAV', { size: wav.size, type: wav.type });
                const b64 = await ctx.blobToBase64(wav);
                ctx.debugLog('Sending MediaRecorder audio over WebSocket', {
                    encodedLength: b64.length,
                    transcriptionAuthority: 'provider',
                });
                ctx.sendToWs({ session_token: ctx.sessionToken, bot_profile: window.growtype_chat[ctx.chatId]['bot_profile'], message: b64, timestamp: Date.now(), type: 'audio' });
                ctx.debugLog('MediaRecorder audio handed to WebSocket sender');
            } catch (error) {
                console.error("[growtype-chat][videocall] Error converting MediaRecorder audio", {
                    name: error && error.name,
                    message: error && error.message,
                    stack: error && error.stack,
                });
                if (typeof ctx.onRecordingError === 'function') ctx.onRecordingError(error);
            }
        }
        audioChunks = [];
        ctx.debugLog('MediaRecorder stopped-state cleanup completed');
    };

    window.startMediaRecorder = () => {
        ctx.debugLog("startMediaRecorder invoked", {
            recorderState: ctx.audioRecorder.val ? ctx.audioRecorder.val.state : null,
        });
        if (ctx.audioRecorder.val && ctx.audioRecorder.val.state === "inactive") {
            ctx.debugLog('Calling MediaRecorder.start');
            ctx.audioRecorder.val.start();
            ctx.debugLog('MediaRecorder.start returned', { recorderState: ctx.audioRecorder.val.state });
        } else {
            ctx.debugLog('MediaRecorder.start skipped because recorder is not inactive');
        }
    };
    window.stopMediaRecorder = () => {
        ctx.debugLog("stopMediaRecorder invoked", {
            recorderState: ctx.audioRecorder.val ? ctx.audioRecorder.val.state : null,
        });
        if (ctx.audioRecorder.val && ctx.audioRecorder.val.state === "recording") {
            ctx.debugLog('Calling MediaRecorder.stop');
            ctx.audioRecorder.val.stop();
            ctx.debugLog('MediaRecorder.stop returned', { recorderState: ctx.audioRecorder.val.state });
        } else {
            ctx.debugLog('MediaRecorder.stop skipped because recorder is not recording');
        }
    };
}


// ── Recording Lifecycle ────────────────────────────────────────────

export function startRecording(ctx) {
    ctx.debugLog("startRecording invoked", {
        isMuted: ctx.isMuted,
        isRecording: ctx.isRecording,
        canInterrupt: ctx.canInterrupt,
        isModalOpen: ctx.isModalOpen,
        socketState: ctx.socket.val ? ctx.socket.val.readyState : null,
    });
    ctx.userSaidSomethingCounter = 0;

    if (ctx.isMuted) { console.error("Recording can not start, is muted"); return; }
    if (ctx.isRecording) { console.error("Recording already running, skipping start"); return; }

    if (ctx.canInterrupt && !ctx.isRecording && ctx.isModalOpen) {
        try {
            ctx.setIsRecording(true);
            ctx.stopAudio();

            const sendStartSession = () => {
                if (ctx.sessionStarted) { ctx.debugLog("Session already started"); return; }
                if (ctx.sessionStartInProgress) { ctx.debugLog("Session start already in progress..."); return; }

                if (ctx.socket.val && ctx.socket.val.readyState === WebSocket.OPEN) {
                    ctx.setSessionStartInProgress(true);
                    if (ctx.onSessionStartRequested) ctx.onSessionStartRequested();
                    const bp = window.growtype_chat[ctx.chatId]['bot_profile'];
                    ctx.debugLog(`Starting persistent session for: ${bp.name || bp.slug || "Unknown"}...`);
                    ctx.sendToWs({ session_token: ctx.sessionToken, bot_profile: bp, voice: ctx.getSelectedVoice(), domain: ctx.sessionDomain, type: "start_session" });
                } else {
                    ctx.debugLog("Waiting for WebSocket to be ready...");
                    setTimeout(sendStartSession, 200);
                }
            };
            sendStartSession();

            if (typeof window.startAudioWorklet === "function") window.startAudioWorklet();
            else if (typeof window.startScriptProcessor === "function") window.startScriptProcessor();
            else if (typeof window.startMediaRecorder === "function") window.startMediaRecorder();

            const modal = $("#growtypeChatVideoCallModal");
            modal.addClass("is-recording");
            modal.find('.e-text[data-type="info"]').removeClass("is-active");
            $(".modal-videocall .btn-record").addClass("is-recording").text(ctx.stopRecordingBtnText);
            ctx.stopLoadingAnimation();
        } catch (error) {
            console.error("Failed to start provider-authoritative microphone streaming:", error);
            ctx.setIsRecording(false);
            $("#growtypeChatVideoCallModal").removeClass("is-recording");
        }
    }
}

/**
 * Finish one automatically detected user turn without changing the
 * user-controlled conversation button to its paused state.
 */
export function finishRecordingTurn(ctx) {
    ctx.debugLog('Finishing captured turn without changing conversation button state', {
        isRecording: ctx.isRecording,
        recorderState: ctx.audioRecorder ? ctx.audioRecorder.state : null,
    });

    if (typeof window.stopAudioWorklet === 'function') {
        window.stopAudioWorklet();
    } else if (typeof window.stopScriptProcessor === 'function') {
        window.stopScriptProcessor();
    } else if (typeof window.stopMediaRecorder === 'function') {
        window.stopMediaRecorder();
    }

    ctx.setIsRecording(false);
}

export function stopRecording(ctx, isMuteParam) {
    const isMute = isMuteParam || false;
    const modal = $("#growtypeChatVideoCallModal");
    ctx.debugLog("stopRecording invoked", {
        isMute: isMute,
        socketState: ctx.socket.val ? ctx.socket.val.readyState : null,
    });

    if (!isMute) ctx.conversationStopped = true;

    if (typeof window.stopAudioWorklet === "function") window.stopAudioWorklet();
    else if (typeof window.stopScriptProcessor === "function") window.stopScriptProcessor();
    else if (typeof window.stopMediaRecorder === "function") window.stopMediaRecorder();

    ctx.stopAudio();

    if (!isMute) {
        ctx.debugLog("Ending persistent OpenAI session...");
        if (ctx.socket.val && ctx.socket.val.readyState === WebSocket.OPEN) {
            ctx.sendToWs({ session_token: ctx.sessionToken, type: 'end_session' });
        } else {
            ctx.debugLog('Skipping end_session because WebSocket is not open');
        }
        ctx.socket.val = null;
        ctx.abortAjax();
        ctx.setSessionStarted(false);
        ctx.setSessionStartInProgress(false);
    }

    modal.removeClass("is-recording");

    $(".modal-videocall .btn-record").removeClass("is-recording").removeClass("is-loading").text(ctx.continueRecordingBtnText);

    ctx.setIsRecording(false);

    if (window.silenceMonitorInterval) { clearInterval(window.silenceMonitorInterval); window.silenceMonitorInterval = null; }
    if (ctx.silenceTimerVal != null) { clearTimeout(ctx.silenceTimerVal); ctx.silenceTimerVal = null; }

    ctx.hideInfoMessage();
}
